import { QueryFunctionContext } from "react-query";
import onBoardingApi from "./onBoardingApi";

export type AggregateEsopReport = {
  externalRequestId: string;
  fromDate: string;
  toDate: string;
  filters?: AggregateEsopReportFilters;
  status: "QUEUED" | "COMPLETED" | "FAILED";
  optionsJobId?: string;
  companyCount?: number;
  generatedAt?: string;
  fetchedAt?: string;
  expiresAt?: string;
  forceRefresh?: boolean;
  failureReason?: string;
  downloadUrl?: string | null;
};

export type AggregateEsopDateRange = {
  fromDate: string;
  toDate: string;
};

export type AggregateEsopReportFilters = {
  vesting: AggregateEsopDateRange;
  exercise: AggregateEsopDateRange;
  expiry: AggregateEsopDateRange;
  liquidity: AggregateEsopDateRange;
};

export type AggregateEsopReportData = {
  schemaVersion: number;
  generatedAt: string;
  fetchedAt: string;
  companyCount: number;
  reportingPeriod: { fromDate: string; toDate: string };
  filters: AggregateEsopReportFilters;
  columns: { key: string; label: string }[];
  rows: Record<string, string | number | null>[];
};

export const requestAggregateEsopReport = async (data: {
  fromDate: string;
  toDate: string;
  filters: AggregateEsopReportFilters;
  forceRefresh?: boolean;
}): Promise<AggregateEsopReport> =>
  onBoardingApi
    .post("adminStats/aggregateEsopReports", data)
    .then((res) => res.data.data);

export const getAggregateEsopReport = async (
  externalRequestId: string,
): Promise<AggregateEsopReport> =>
  onBoardingApi
    .get(`esopAggregateReports/${externalRequestId}`)
    .then((res) => res.data.data);

export const getLatestAggregateEsopReport =
  async (): Promise<AggregateEsopReport | null> =>
    onBoardingApi
      .get("esopAggregateReports/latest")
      .then((res) => res.data.data);

export const getActiveAggregateEsopReports = async (): Promise<
  AggregateEsopReport[]
> =>
  onBoardingApi.get("esopAggregateReports/active").then((res) => res.data.data);

export const getAggregateEsopReportData = async (
  externalRequestId: string,
): Promise<AggregateEsopReportData> =>
  onBoardingApi
    .get(`esopAggregateReports/${externalRequestId}/data`)
    .then((res) => res.data.data);

export async function getEsopCompanyStats(
  context?: QueryFunctionContext,
): Promise<any> {
  return onBoardingApi.get(`adminStats/esopStats`).then((res) => res.data.data);
}
export async function esopAdminDetails(companyId: string): Promise<void> {
  return onBoardingApi
    .get(`adminStats/esopAdminDetails?companyId=${companyId}`)
    .then((res) => {
      return res.data.data;
    })
    .catch((error: any) => error.data);
}
export async function esopCompanyDetails(companyId: string): Promise<void> {
  return onBoardingApi
    .get(`adminStats/esopCompanyDetails?companyId=${companyId}`)
    .then((res) => res.data.data)
    .catch((error: any) => error.data);
}
export async function esopDeleteUser(data: any): Promise<void> {
  return onBoardingApi
    .delete(`adminStats/esopDeleteUser`, { data })
    .then((res) => res.data.data)
    .catch((error: any) => error.data);
}
export async function esopAddBillDetails(data: any): Promise<void> {
  return onBoardingApi
    .post(`adminStats/addBillDetails`, { data })
    .then((res) => res.data)
    .catch((error: any) => error.data);
}
export async function esopLiveDate(data: any): Promise<void> {
  return onBoardingApi
    .patch(`adminStats/updateCompanyDetails`, { data })
    .then((res) => res.data)
    .catch((error: any) => error.data);
}
export async function esopGenBillReport(data: any): Promise<void> {
  return onBoardingApi
    .get(
      `adminStats/getBillReport?companyId=${data.companyId}&startDate=${data.startDate}&endDate=${data.endDate}&forBilling=${data.forBilling}`,
    )
    .then((res) => res.data)
    .catch((error: any) => error.data);
}
export async function esopGenFullBillReport(data: any): Promise<void> {
  return onBoardingApi
    .get(
      `adminStats/getFullBillReport?startDate=${data.startDate}&endDate=${data.endDate}&forBilling=${data.forBilling}`,
    )
    .then((res) => res.data)
    .catch((error: any) => error.data);
}

export async function updateCompanyBillingDetails(body: any): Promise<any> {
  return onBoardingApi
    .patch("adminStats/updateCompanyDetails", body)
    .then((res) => res.data?.data)
    .catch((error: any) => error?.data);
}
